import type { IntentAnswers, IntentQuestion } from "./companyIntent.types";
import { toIntentAnswerPayload } from "./companyIntentAnswers.util";
import {
  CURRENT_INTENT_QUESTION_IDS,
  INTENT_QUESTION_DEFINITIONS,
  INTENT_QUESTION_OPTION_COUNT,
  INTENT_QUESTIONNAIRE_VERSION,
  Q15_PRIORITY_MARKET_ID,
  Q15_REGION_BAND_TAGS,
  getIntentQuestionDefinition,
  getMaxSelections,
  hasAnswerGroups,
  resolveIntentSelectionMode,
} from "./intentQuestionnaireDimensions";
import { displayOptionsForIntentQuestion } from "./intentQuestionOptions.util";

export type AdminIntentJsonOption = {
  id: string;
  label: string;
  selected?: boolean;
  tags?: string[];
};

export type AdminIntentJsonAnswerGroup = {
  id: string;
  prompt: string;
  selectionMode?: "single" | "multiple";
  catalogBacked?: boolean;
  options: AdminIntentJsonOption[];
};

export type AdminIntentJsonQuestion = {
  id: string;
  prompt: string;
  selectionMode: "single" | "multiple";
  followUpText?: string;
  options: AdminIntentJsonOption[];
  answerGroups?: AdminIntentJsonAnswerGroup[];
};

export type AdminIntentJsonQuestionSource = {
  id: string;
  prompt: string;
  selectionMode: "single" | "multiple";
  followUpText?: string | null;
  decisionSignals?: string[];
  maxSelections?: number;
  options: AdminIntentJsonOption[];
  answerGroups?: Array<{
    id: string;
    prompt: string;
    selectionMode?: "single" | "multiple";
    catalogBacked?: boolean;
    selectedLabels?: string[];
    options: AdminIntentJsonOption[];
  }>;
};

export type AdminIntentSelectedJson = {
  additionalInfo: string;
  questions: AdminIntentJsonQuestion[];
};

export type AdminIntentJsonUpdateItem = {
  id: string;
  prompt: string;
  followUpText: string | null;
  options: { id: string; label: string; selected: boolean }[];
  answerGroups: {
    id: string;
    prompt: string;
    options: { id: string; label: string; selected: boolean }[];
  }[];
};

export type ParseAdminIntentSelectedJsonResult =
  | { ok: true; value: AdminIntentSelectedJson }
  | { ok: false; error: string };

function prettyJson(value: unknown): string {
  return `${JSON.stringify(value, null, 2)}\n`;
}

function optionLetter(index: number): string {
  return String.fromCharCode(97 + index);
}

function defaultOptionTags(questionId: string, option: AdminIntentJsonOption, index: number): string[] {
  if (option.tags?.length) return option.tags;
  if (questionId === Q15_PRIORITY_MARKET_ID) {
    const tag = Q15_REGION_BAND_TAGS[index];
    return tag ? [tag] : ["region_europe_uk"];
  }
  const fromId = option.id.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return fromId ? [fromId] : [`${questionId}_${optionLetter(index)}`];
}

function toGenerateShapeQuestion(
  item: AdminIntentJsonQuestionSource,
  withSelected: boolean,
): Record<string, unknown> {
  const def = getIntentQuestionDefinition(item.id);
  const selectionMode = def?.selectionMode ?? (item.selectionMode === "single" ? "single" : "multiple");
  const followUp = def?.followUp
    ? {
      enabled: true,
      optional: true,
      type: "textarea",
      prompt: def.followUp.prompt,
      maxLength: def.followUp.maxLength,
    }
    : undefined;
  const options = (item.options ?? []).map((option, index) => ({
    id: option.id,
    label: option.label,
    tags: defaultOptionTags(item.id, option, index),
    ...(withSelected ? { selected: Boolean(option.selected) } : {}),
  }));
  return {
    id: item.id,
    prompt: item.prompt,
    selectionMode,
    ...(def?.maxSelections ? { maxSelections: def.maxSelections } : {}),
    decisionSignals: item.decisionSignals?.length
      ? item.decisionSignals
      : [...(def?.decisionSignals ?? [])],
    options,
    ...(followUp ? { followUp } : {}),
    ...(withSelected ? { followUpText: item.followUpText?.trim() ?? "" } : {}),
  };
}

export function stringifyAdminIntentAllOptionsJson(
  items: AdminIntentJsonQuestionSource[],
): string {
  return prettyJson({
    version: INTENT_QUESTIONNAIRE_VERSION,
    questions: items.map((item) => toGenerateShapeQuestion(item, false)),
  });
}

export function stringifyAdminIntentSelectedOptionsJson(
  items: AdminIntentJsonQuestionSource[],
  additionalInfo: string | null | undefined,
): string {
  return prettyJson({
    version: INTENT_QUESTIONNAIRE_VERSION,
    additionalInfo: additionalInfo?.trim() ?? "",
    questions: items.map((item) => toGenerateShapeQuestion(item, true)),
  });
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function parseOption(value: unknown): AdminIntentJsonOption | null {
  const rec = asRecord(value);
  if (!rec) return null;
  const id = typeof rec.id === "string" ? rec.id.trim() : "";
  if (!id) return null;
  const label = typeof rec.label === "string" && rec.label.trim() ? rec.label.trim() : id;
  const tags = Array.isArray(rec.tags)
    ? rec.tags.filter((tag): tag is string => typeof tag === "string" && Boolean(tag.trim()))
    : undefined;
  return {
    id,
    label,
    selected: rec.selected === true,
    ...(tags?.length ? { tags } : {}),
  };
}

function parseAnswerGroup(value: unknown): AdminIntentJsonAnswerGroup | null {
  const rec = asRecord(value);
  if (!rec) return null;
  const id = typeof rec.id === "string" ? rec.id.trim() : "";
  if (!id) return null;
  const prompt = typeof rec.prompt === "string" ? rec.prompt.trim() : "";
  const options = Array.isArray(rec.options)
    ? rec.options.map(parseOption).filter((option): option is AdminIntentJsonOption => Boolean(option))
    : [];
  return {
    id,
    prompt,
    selectionMode: rec.selectionMode === "single" ? "single" : "multiple",
    catalogBacked: rec.catalogBacked === true,
    options,
  };
}

function parseQuestion(value: unknown): AdminIntentJsonQuestion | null {
  const rec = asRecord(value);
  if (!rec) return null;
  const id = typeof rec.id === "string" ? rec.id.trim() : "";
  if (!id) return null;
  const prompt = typeof rec.prompt === "string" ? rec.prompt.trim() : "";
  const followUpText = typeof rec.followUpText === "string" ? rec.followUpText : "";
  const options = Array.isArray(rec.options)
    ? rec.options.map(parseOption).filter((option): option is AdminIntentJsonOption => Boolean(option))
    : [];
  const answerGroups = Array.isArray(rec.answerGroups)
    ? rec.answerGroups
      .map(parseAnswerGroup)
      .filter((group): group is AdminIntentJsonAnswerGroup => Boolean(group))
    : [];
  return {
    id,
    prompt,
    selectionMode: rec.selectionMode === "single" ? "single" : "multiple",
    followUpText,
    options,
    answerGroups,
  };
}

export function canonicalIntentQuestionId(rawId: string, index: number): string {
  const trimmed = rawId.trim();
  if ((CURRENT_INTENT_QUESTION_IDS as readonly string[]).includes(trimmed)) return trimmed;
  const match = trimmed.match(/^(?:iq|q)(\d{1,2})$/i);
  if (match) {
    const number = Number(match[1]);
    if (number >= 1 && number <= CURRENT_INTENT_QUESTION_IDS.length) {
      return CURRENT_INTENT_QUESTION_IDS[number - 1]!;
    }
  }
  return CURRENT_INTENT_QUESTION_IDS[index] ?? trimmed;
}

export function normalizeAdminIntentJsonQuestions(
  questions: AdminIntentJsonQuestion[],
): AdminIntentJsonQuestion[] {
  return questions.map((question, questionIndex) => {
    const id = canonicalIntentQuestionId(question.id, questionIndex);
    const def = getIntentQuestionDefinition(id);
    const questionNumber = (CURRENT_INTENT_QUESTION_IDS as readonly string[]).indexOf(id);
    const shortNumber = questionNumber >= 0 ? questionNumber + 1 : questionIndex + 1;
    const options = question.options.map((option, optionIndex) => {
      const letter = optionLetter(optionIndex);
      const remappedId = /^q\d{1,2}_[a-z]$/i.test(option.id)
        ? option.id.toLowerCase()
        : `q${shortNumber}_${letter}`;
      return {
        ...option,
        id: remappedId,
        tags: defaultOptionTags(id, { ...option, id: remappedId }, optionIndex),
      };
    });
    return {
      ...question,
      id,
      selectionMode: def?.selectionMode ?? question.selectionMode,
      options,
    };
  });
}

export function unansweredAdminIntentQuestionIds(questions: AdminIntentJsonQuestion[]): string[] {
  return questions
    .filter((question) => {
      const selected = question.options.filter((option) => option.selected);
      const groupSelected = (question.answerGroups ?? []).some((group) =>
        group.options.some((option) => option.selected),
      );
      if (groupSelected) return false;
      if (selected.length === 0) return true;
      const mode = getIntentQuestionDefinition(question.id)?.selectionMode ?? question.selectionMode;
      if (mode === "single" && selected.length !== 1) return true;
      const maxSelections = getMaxSelections(question.id);
      if (maxSelections != null && selected.length > maxSelections) return true;
      return false;
    })
    .map((question) => question.id);
}

export function coerceAdminIntentQuestionsForImport(questions: unknown): unknown[] {
  if (!Array.isArray(questions)) return [];
  const parsed = questions
    .map(parseQuestion)
    .filter((question): question is AdminIntentJsonQuestion => Boolean(question));
  return adminIntentQuestionsForImport(normalizeAdminIntentJsonQuestions(parsed));
}

export function parseAdminIntentSelectedJson(jsonText: string): ParseAdminIntentSelectedJsonResult {
  const trimmed = jsonText.trim();
  if (!trimmed) {
    return { ok: false, error: "Paste JSON before submitting." };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed) as unknown;
  } catch {
    return { ok: false, error: "Invalid JSON. Use double quotes and no trailing commas." };
  }

  const rec = asRecord(parsed);
  if (!rec) {
    return { ok: false, error: "JSON must be a single object, not an array." };
  }

  if (!Array.isArray(rec.questions) || rec.questions.length === 0) {
    return { ok: false, error: "JSON must include a questions array." };
  }

  const questions = normalizeAdminIntentJsonQuestions(
    rec.questions
      .map(parseQuestion)
      .filter((question): question is AdminIntentJsonQuestion => Boolean(question)),
  );

  if (questions.length === 0) {
    return { ok: false, error: "Each question needs an id and options." };
  }

  const additionalInfo =
    typeof rec.additionalInfo === "string" ? rec.additionalInfo : "";

  return {
    ok: true,
    value: { additionalInfo, questions },
  };
}

export function selectedJsonToIntentAnswers(questions: AdminIntentJsonQuestion[]): IntentAnswers {
  const answers: IntentAnswers = {};
  for (const question of questions) {
    const selectedIds = question.options.filter((option) => option.selected).map((option) => option.id);
    const groupSelections: Record<string, string[]> = {};
    const groupRegionIds: Record<string, string[]> = {};
    for (const group of question.answerGroups ?? []) {
      const ids = group.options.filter((option) => option.selected).map((option) => option.id);
      if (ids.length === 0) continue;
      groupSelections[group.id] = ids;
      if (group.catalogBacked) {
        groupRegionIds[group.id] = ids;
      }
    }
    answers[question.id] = toIntentAnswerPayload({
      selectedIds,
      selectionMode: resolveIntentSelectionMode(question.id, question.selectionMode),
      maxSelections: getMaxSelections(question.id),
      followUpText: question.followUpText,
      groupSelections,
      groupRegionIds,
      usesAnswerGroups: hasAnswerGroups(question.id) || Boolean(question.answerGroups?.length),
    });
    const payload = answers[question.id];
    const empty =
      payload === "" || (Array.isArray(payload) && payload.length === 0);
    if (empty) {
      delete answers[question.id];
    }
  }
  return answers;
}

export function overlaySelectedOntoUpdateItems(
  source: AdminIntentJsonQuestionSource[],
  parsedQuestions: AdminIntentJsonQuestion[],
): AdminIntentJsonUpdateItem[] {
  const parsedById = new Map(parsedQuestions.map((question) => [question.id, question]));
  return source.map((item) => {
    const parsed = parsedById.get(item.id);
    const parsedOptionSelected = new Map(
      (parsed?.options ?? []).map((option) => [option.id, Boolean(option.selected)]),
    );
    const parsedGroups = new Map((parsed?.answerGroups ?? []).map((group) => [group.id, group]));
    return {
      id: item.id,
      prompt: item.prompt.trim() || item.id,
      followUpText: parsed?.followUpText?.trim() || item.followUpText?.trim() || null,
      options: (item.options ?? []).map((option) => ({
        id: option.id,
        label: option.label.trim() || option.id,
        selected: parsedOptionSelected.has(option.id)
          ? Boolean(parsedOptionSelected.get(option.id))
          : Boolean(option.selected),
      })),
      answerGroups: (item.answerGroups ?? []).map((group) => {
        const parsedGroup = parsedGroups.get(group.id);
        const groupSelected = new Map(
          (parsedGroup?.options ?? []).map((option) => [option.id, Boolean(option.selected)]),
        );
        return {
          id: group.id,
          prompt: group.prompt.trim() || group.id,
          options: (group.options ?? []).map((option) => ({
            id: option.id,
            label: option.label.trim() || option.id,
            selected: groupSelected.has(option.id)
              ? Boolean(groupSelected.get(option.id))
              : Boolean(option.selected),
          })),
        };
      }),
    };
  });
}

export type IntentJsonAnswerState = {
  selectedIds?: string[];
  followUpText?: string;
  groupSelections?: Record<string, string[]>;
  groupRegionIds?: Record<string, string[]>;
};

export function intentQuestionsToJsonSource(
  questions: IntentQuestion[],
  answers: Record<string, IntentJsonAnswerState | undefined>,
): AdminIntentJsonQuestionSource[] {
  return questions.map((question) => {
    const answer = answers[question.id];
    const selectedSet = new Set(answer?.selectedIds ?? []);
    const displayOptions = displayOptionsForIntentQuestion(question);
    return {
      id: question.id,
      prompt: question.prompt,
      selectionMode: resolveIntentSelectionMode(question.id, question.selectionMode),
      followUpText: answer?.followUpText ?? null,
      decisionSignals: question.decisionSignals,
      maxSelections: question.maxSelections,
      options: displayOptions.map((option) => ({
        id: option.id,
        label: option.label,
        selected: selectedSet.has(option.id),
        ...(option.tags?.length ? { tags: option.tags } : {}),
      })),
      answerGroups: (question.answerGroups ?? []).map((group) => {
        const selectedIds = [
          ...(answer?.groupSelections?.[group.id] ?? []),
          ...(answer?.groupRegionIds?.[group.id] ?? []),
        ];
        const selectedIdSet = new Set(selectedIds);
        return {
          id: group.id,
          prompt: group.prompt,
          selectionMode: group.selectionMode === "single" ? "single" : "multiple",
          catalogBacked: group.inputType === "catalog_geography",
          selectedLabels: selectedIds,
          options: (group.options ?? []).map((option) => ({
            id: option.id,
            label: option.label,
            selected: selectedIdSet.has(option.id),
          })),
        };
      }),
    };
  });
}

const Q15_TEMPLATE_OPTION_LABELS = [
  "Europe and the UK",
  "India and South Asia",
  "Middle East and Africa",
  "Southeast Asia and APAC",
] as const;

function buildTemplateQuestions(withSelectedFlag: boolean): AdminIntentJsonQuestion[] {
  return INTENT_QUESTION_DEFINITIONS.map((def, questionIndex) => {
    const shortId = `q${questionIndex + 1}`;
    const isQ15 = def.id === Q15_PRIORITY_MARKET_ID;
    const options: AdminIntentJsonOption[] = isQ15
      ? Q15_REGION_BAND_TAGS.map((tag, index) => ({
        id: `${shortId}_${optionLetter(index)}`,
        label: Q15_TEMPLATE_OPTION_LABELS[index] ?? tag,
        tags: [tag],
        ...(withSelectedFlag ? { selected: false } : {}),
      }))
      : Array.from({ length: INTENT_QUESTION_OPTION_COUNT }, (_, index) => {
        const letter = optionLetter(index);
        return {
          id: `${shortId}_${letter}`,
          label: `Rewrite this ${def.title.toLowerCase()} option`,
          tags: [`${shortId}_${letter}`],
          ...(withSelectedFlag ? { selected: false } : {}),
        };
      });

    return {
      id: def.id,
      prompt: def.userPromptGuide,
      selectionMode: def.selectionMode,
      decisionSignals: [...def.decisionSignals],
      ...(withSelectedFlag ? { followUpText: "" } : {}),
      options,
    };
  });
}

export function buildAdminIntentAllOptionsTemplateItems(): AdminIntentJsonQuestionSource[] {
  return buildTemplateQuestions(false);
}

export const ADMIN_INTENT_ALL_OPTIONS_JSON_TEMPLATE = stringifyAdminIntentAllOptionsJson(
  buildAdminIntentAllOptionsTemplateItems(),
);

export const ADMIN_INTENT_SELECTED_OPTIONS_JSON_TEMPLATE = stringifyAdminIntentSelectedOptionsJson(
  buildTemplateQuestions(true),
  "",
);

export const ADMIN_INTENT_GPT_INSTRUCTIONS = `Rewrite this GTSetu onboarding questionnaire JSON for the company I describe. Match the in-app generate format.

Rules:
- Keep version 9 and every question id exactly as given (${CURRENT_INTENT_QUESTION_IDS.join(", ")}).
- Keep selectionMode, decisionSignals, maxSelections and followUp exactly as given (followUp only on q15_deal_structure_preferences).
- Each question has exactly 4 options: { "id", "label", "tags" }. Option ids stay unique (q1_a, q1_b, q1_c, q1_d, then q2_a...).
- Each option has 1–4 lowercase snake_case tags. Do not use: good, bad, yes, no, option_1, other.
- For q15_deal_structure_preferences keep one region-band tag per option: ${Q15_REGION_BAND_TAGS.join(", ")}.
- Do not return answerGroups. Write prompts in second person ("your company").
- For selected-options JSON also set selected true/false. Single = one true. q11_partner_type_sought = at most two true.
- Return JSON only.`;

export function adminIntentQuestionsForImport(questions: AdminIntentJsonQuestion[]): unknown[] {
  return questions.map((question) => ({
    id: question.id,
    prompt: question.prompt,
    selectionMode: question.selectionMode,
    options: question.options.map((option) => ({
      id: option.id,
      label: option.label,
      ...(option.tags?.length ? { tags: option.tags } : {}),
    })),
    ...(question.answerGroups?.length
      ? {
        answerGroups: question.answerGroups.map((group) => ({
          id: group.id,
          prompt: group.prompt,
          selectionMode: group.selectionMode,
          options: group.options.map((option) => ({
            id: option.id,
            label: option.label,
            ...(option.tags?.length ? { tags: option.tags } : {}),
          })),
          ...(group.catalogBacked ? { inputType: "catalog_geography" as const } : {}),
        })),
      }
      : {}),
  }));
}
